import { useEffect, useMemo, useRef, useState } from 'react';
import {
  UploadCloud,
  Download,
  Loader2,
  FileSpreadsheet,
  ArrowLeft,
  CheckCircle2,
} from 'lucide-react';
import {
  BULK_IMPORT_COLUMNS,
  type BulkImportReport,
  type BulkRowResult,
  type BulkRowStatus,
} from '@pms/shared';
import { StepIndicator } from '@/components/ui/StepIndicator';
import { SlideOver } from '@/components/ui/SlideOver';
import { usePlatformOperators } from '@/hooks/api/usePlatform';
import { useValidateBulkImport, useCommitBulkImport } from '@/hooks/api/useBulkImport';
import { toCsv, downloadCsv, type CsvColumn } from '@/lib/csv';
import { extractErrorMessage } from '@/lib/error';
import { showToast } from '@/lib/toast';
import { cn } from '@/lib/utils';

interface BulkImportWizardProps {
  isOpen: boolean;
  onClose: () => void;
  /** Pre-bind every row to one operator (e.g. opened from a Group detail page). */
  defaultTargetTenantId?: string;
}

type WizardStep = 0 | 1 | 2; // Upload · Preview · Report

const STEPS = [{ label: 'Upload' }, { label: 'Preview' }, { label: 'Report' }];

// ─── Per-status presentation ────────────────────────────────────────────────────

const STATUS_META: Record<BulkRowStatus, { label: string; cls: string }> = {
  valid: { label: 'Valid', cls: 'bg-emerald-100 text-emerald-700' },
  created: { label: 'Created', cls: 'bg-green-100 text-green-700' },
  exists: { label: 'Exists', cls: 'bg-slate-100 text-slate-600' },
  duplicate: { label: 'Duplicate', cls: 'bg-amber-100 text-amber-700' },
  error: { label: 'Error', cls: 'bg-red-100 text-red-700' },
  failed: { label: 'Failed', cls: 'bg-red-100 text-red-700' },
};

function StatusBadge({ status }: { status: BulkRowStatus }) {
  const meta = STATUS_META[status];
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        meta.cls,
      )}
    >
      {meta.label}
    </span>
  );
}

// ─── Per-row results table (shared by Preview + Report) ─────────────────────────

function RowsTable({ rows }: { rows: BulkRowResult[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-100 text-sm" aria-label="Import rows">
        <thead>
          <tr className="bg-gray-50">
            {['Row', 'Property', 'Operator', 'Location', 'Status', 'Reason'].map((c) => (
              <th
                key={c}
                scope="col"
                className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r, i) => (
            <tr key={`${r.row}-${i}`} className="align-top">
              <td className="px-3 py-2 text-gray-500 tabular-nums">{r.row}</td>
              <td className="px-3 py-2 font-medium text-gray-900">{r.name || '—'}</td>
              <td className="px-3 py-2 text-gray-600">{r.operator || '—'}</td>
              <td className="px-3 py-2 text-gray-600">{r.location || '—'}</td>
              <td className="px-3 py-2">
                <StatusBadge status={r.status} />
              </td>
              <td className="px-3 py-2 text-gray-500">{r.reason || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Small count-chip ───────────────────────────────────────────────────────────

function CountChip({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium',
        tone,
      )}
    >
      <span className="tabular-nums font-semibold">{value}</span>
      {label}
    </span>
  );
}

export function BulkImportWizard({
  isOpen,
  onClose,
  defaultTargetTenantId,
}: BulkImportWizardProps) {
  const [step, setStep] = useState<WizardStep>(0);
  const [file, setFile] = useState<File | null>(null);
  const [targetTenantId, setTargetTenantId] = useState<string>('');
  const [preview, setPreview] = useState<BulkImportReport | null>(null);
  const [report, setReport] = useState<BulkImportReport | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  // One Idempotency-Key per wizard session — a retried commit replays, never
  // double-creates. Re-minted whenever the wizard (re)opens or on "Import another".
  const idempotencyKey = useRef<string>('');

  const operatorsQuery = usePlatformOperators();
  const validateMutation = useValidateBulkImport();
  const commitMutation = useCommitBulkImport();

  // Reset all state + mint a fresh idempotency key on open.
  useEffect(() => {
    if (!isOpen) return;
    setStep(0);
    setFile(null);
    setTargetTenantId(defaultTargetTenantId ?? '');
    setPreview(null);
    setReport(null);
    validateMutation.reset();
    commitMutation.reset();
    idempotencyKey.current = crypto.randomUUID();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultTargetTenantId]);

  function handlePickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0] ?? null;
    // Reset so re-selecting the same file re-triggers onChange.
    e.target.value = '';
    if (picked) setFile(picked);
  }

  function downloadTemplate() {
    const columns: CsvColumn[] = BULK_IMPORT_COLUMNS.map((c) => ({ key: c, header: c }));
    const csv = toCsv([] as Record<string, unknown>[], columns);
    downloadCsv('bulk-property-import-template.csv', csv);
  }

  async function handleValidate() {
    if (!file) return;
    try {
      const result = await validateMutation.mutateAsync({
        file,
        ...(targetTenantId ? { targetTenantId } : {}),
      });
      setPreview(result);
      setStep(1);
    } catch (err) {
      showToast(
        extractErrorMessage(err) || 'Could not read that spreadsheet.',
        'error',
      );
    }
  }

  async function handleCommit() {
    if (!file) return;
    try {
      const result = await commitMutation.mutateAsync({
        file,
        ...(targetTenantId ? { targetTenantId } : {}),
        idempotencyKey: idempotencyKey.current,
      });
      setReport(result);
      setStep(2);
    } catch (err) {
      showToast(extractErrorMessage(err) || 'Import failed.', 'error');
    }
  }

  function backToUpload() {
    setStep(0);
    setPreview(null);
    validateMutation.reset();
  }

  function importAnother() {
    setStep(0);
    setFile(null);
    setPreview(null);
    setReport(null);
    validateMutation.reset();
    commitMutation.reset();
    // Fresh session ⇒ fresh idempotency key.
    idempotencyKey.current = crypto.randomUUID();
  }

  // ── Derived preview copy ──────────────────────────────────────────────────────
  const previewSummary = preview?.summary;
  const skippedPreview = previewSummary
    ? previewSummary.exists + previewSummary.duplicate + previewSummary.errors
    : 0;
  const canCommit = (previewSummary?.valid ?? 0) > 0;

  const previewAnnouncement = useMemo(() => {
    if (!previewSummary) return '';
    if (previewSummary.total === 0) return 'No data rows found in the file.';
    return `${previewSummary.valid} will be created, ${skippedPreview} skipped.`;
  }, [previewSummary, skippedPreview]);

  const reportAnnouncement = report
    ? `${report.summary.created ?? 0} created, ${
        report.summary.exists + report.summary.duplicate
      } skipped, ${report.summary.failed ?? 0} failed.`
    : '';

  function downloadReportCsv() {
    if (!report) return;
    const rows: Record<string, unknown>[] = report.rows.map((r) => ({
      row: r.row,
      name: r.name ?? '',
      operator: r.operator ?? '',
      location: r.location ?? '',
      status: r.status,
      reason: r.reason ?? '',
      propertyId: r.propertyId ?? '',
    }));
    const columns: CsvColumn[] = [
      { key: 'row', header: 'Row' },
      { key: 'name', header: 'Name' },
      { key: 'operator', header: 'Operator' },
      { key: 'location', header: 'Location' },
      { key: 'status', header: 'Status' },
      { key: 'reason', header: 'Reason' },
      { key: 'propertyId', header: 'Property ID' },
    ];
    downloadCsv(
      `bulk-import-report-${new Date().toISOString().slice(0, 10)}.csv`,
      toCsv(rows, columns),
    );
  }

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={onClose}
      title="Bulk import properties"
      width="max-w-4xl"
    >
      <StepIndicator steps={STEPS} currentStep={step} />

      {/* Live region — announces preview/report outcomes to assistive tech. */}
      <div className="sr-only" role="status" aria-live="polite">
        {step === 1 ? previewAnnouncement : step === 2 ? reportAnnouncement : ''}
      </div>

      {/* ── Step 1: Upload ─────────────────────────────────────────────────────── */}
      {step === 0 && (
        <div className="space-y-6">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-gray-600">
              Upload a CSV or Excel file — one property per row.
            </p>
            <button
              type="button"
              onClick={downloadTemplate}
              className="inline-flex items-center gap-2 whitespace-nowrap px-3 py-1.5 text-sm font-medium text-brand-700 border border-brand-300 rounded-lg hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <Download size={15} aria-hidden="true" />
              Download template
            </button>
          </div>

          {/* Drop-zone / file picker (button-driven ⇒ keyboard operable). */}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx"
              onChange={handlePickFile}
              className="sr-only"
              aria-hidden="true"
              tabIndex={-1}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center transition-colors hover:border-brand-400 hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              {file ? (
                <>
                  <FileSpreadsheet size={28} className="text-brand-600" aria-hidden="true" />
                  <span className="text-sm font-semibold text-gray-900">{file.name}</span>
                  <span className="text-xs text-gray-500">Click to choose a different file</span>
                </>
              ) : (
                <>
                  <UploadCloud size={28} className="text-gray-400" aria-hidden="true" />
                  <span className="text-sm font-semibold text-gray-700">
                    Choose a .csv or .xlsx file
                  </span>
                  <span className="text-xs text-gray-500">Up to 1,000 rows</span>
                </>
              )}
            </button>
          </div>

          {/* Target operator */}
          <div>
            <label
              htmlFor="bulk-target-operator"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Target operator
            </label>
            <select
              id="bulk-target-operator"
              value={targetTenantId}
              onChange={(e) => setTargetTenantId(e.target.value)}
              disabled={!!defaultTargetTenantId}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-100 disabled:text-gray-500"
            >
              <option value="">Create from each row&apos;s operator column</option>
              {operatorsQuery.data?.map((op) => (
                <option key={op.tenantId} value={op.tenantId}>
                  {op.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Leave as the default to resolve (or create) an operator per row, or pick one
              operator to attach every row to it.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleValidate()}
              disabled={!file || validateMutation.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {validateMutation.isPending && (
                <Loader2 size={16} className="animate-spin" aria-hidden="true" />
              )}
              {validateMutation.isPending ? 'Reading rows…' : 'Validate file'}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2: Preview ────────────────────────────────────────────────────── */}
      {step === 1 && previewSummary && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <CountChip label="valid" value={previewSummary.valid} tone="bg-emerald-100 text-emerald-700" />
            <CountChip label="exists" value={previewSummary.exists} tone="bg-slate-100 text-slate-600" />
            <CountChip label="duplicate" value={previewSummary.duplicate} tone="bg-amber-100 text-amber-700" />
            <CountChip label="errors" value={previewSummary.errors} tone="bg-red-100 text-red-700" />
            {!!previewSummary.operatorsToCreate && (
              <CountChip
                label="new operators"
                value={previewSummary.operatorsToCreate}
                tone="bg-brand-100 text-brand-700"
              />
            )}
          </div>

          {previewSummary.total === 0 ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-center text-sm text-amber-800">
              No data rows found — the file has only a header row. Fix the file and re-upload.
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-700" aria-hidden="true">
                <span className="font-semibold">{previewSummary.valid}</span> will be created
                {' · '}
                <span className="font-semibold">{skippedPreview}</span> skipped
              </p>
              <RowsTable rows={preview.rows} />
            </>
          )}

          {!canCommit && previewSummary.total > 0 && (
            <p id="bulk-no-valid-reason" className="text-sm text-red-700">
              No valid rows — nothing to import. Fix the flagged rows and re-upload.
            </p>
          )}

          <div className="flex items-center justify-between gap-2 pt-2">
            <button
              type="button"
              onClick={backToUpload}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <ArrowLeft size={15} aria-hidden="true" />
              Back / re-upload
            </button>
            <button
              type="button"
              onClick={() => void handleCommit()}
              disabled={!canCommit || commitMutation.isPending}
              aria-disabled={!canCommit || commitMutation.isPending}
              aria-describedby={!canCommit ? 'bulk-no-valid-reason' : undefined}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {commitMutation.isPending && (
                <Loader2 size={16} className="animate-spin" aria-hidden="true" />
              )}
              {commitMutation.isPending
                ? `Importing ${previewSummary.valid}…`
                : `Import ${previewSummary.valid} valid row${previewSummary.valid === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 3: Report ─────────────────────────────────────────────────────── */}
      {step === 2 && report && (
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
            <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-green-600" aria-hidden="true" />
            <div>
              <p className="text-sm font-semibold text-green-800">Import complete</p>
              <p className="text-sm text-green-700">
                {report.summary.created ?? 0} created ·{' '}
                {report.summary.exists + report.summary.duplicate} skipped
                {(report.summary.failed ?? 0) > 0 && <> · {report.summary.failed} failed</>}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <CountChip label="created" value={report.summary.created ?? 0} tone="bg-green-100 text-green-700" />
            <CountChip label="exists" value={report.summary.exists} tone="bg-slate-100 text-slate-600" />
            <CountChip label="duplicate" value={report.summary.duplicate} tone="bg-amber-100 text-amber-700" />
            {(report.summary.failed ?? 0) > 0 && (
              <CountChip label="failed" value={report.summary.failed ?? 0} tone="bg-red-100 text-red-700" />
            )}
            {report.summary.errors > 0 && (
              <CountChip label="errors" value={report.summary.errors} tone="bg-red-100 text-red-700" />
            )}
          </div>

          {(report.summary.created ?? 0) === 0 && (
            <p className="text-sm text-gray-600">
              Nothing new to import — every row already exists or was skipped.
            </p>
          )}

          <RowsTable rows={report.rows} />

          <div className="flex items-center justify-between gap-2 pt-2">
            <button
              type="button"
              onClick={downloadReportCsv}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-brand-700 border border-brand-300 rounded-lg hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <Download size={15} aria-hidden="true" />
              Download report CSV
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={importAnother}
                className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              >
                Import another
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </SlideOver>
  );
}
